import React, { useEffect, useState } from 'react';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { JournalEntry } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { hasSourceDocument, mustReverseFromSource, readableDescription, referenceLabel, referenceLabelInline } from '../../utils/journalEntryText';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { saveBlob } from '../../utils/saveBlob';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { AuthService } from '../../services/auth.service';
import { LedgerCloseSettingsService } from '../../services/ledger-close-settings.service';
import { isCurrentUser } from '../../utils/currentUser';
import { helpWhy } from '../../help/why';
import { departmentLabel, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import { SegmentService } from '../../services/segment.service';
import { Segment } from '../../interfaces/accounting.interface';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const getStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
        case 'DRAFT': return 'warning';
        case 'POSTED': return 'success';
        case 'VOID': return 'error';
        default: return 'default';
    }
};

const JournalEntryDetail: React.FC = () => {
    const canManage = usePermission(PERMISSIONS.JOURNAL_ENTRY_MANAGE); // create / duplicate / attach
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST); // post / void / reverse = JOURNAL_ENTRY_POST server-side
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [entry, setEntry] = useState<JournalEntry | null>(null);
    const [loading, setLoading] = useState(true);
    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [voiding, setVoiding] = useState(false);
    const [orderNumbers, setOrderNumbers] = useState<Record<string, string>>({}); // B4-0 (M2): order id -> number
    // ---- B4-H: department names for the line tags ----
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution());
    const departmentNames = React.useMemo(
        () => Object.fromEntries(departmentOptions.map(d => [d.id, departmentLabel(d)])) as Record<string, string>,
        [departmentOptions]);
    // ---- end B4-H ----
    const [segmentNames, setSegmentNames] = useState<Record<string, string>>({}); // segment id -> "CODE - Name"

    const fetchEntry = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await JournalEntryService.getEntryById(id);
            setEntry(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load journal entry');
            console.error('Error fetching journal entry:', err);
        } finally {
            setLoading(false);
        }
    };

    // B4-0 (M2): resolve the numbers of the orders the lines are tagged with (best effort; falls back to the id).
    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        const ids = Array.from(new Set((entry?.lines ?? []).map(l => l.buyerOrderId).filter((x): x is string => !!x)));
        if (!institutionId || ids.length === 0) return;
        ids.forEach(oid => {
            BuyerOrderService.getById(institutionId, oid)
                .then(o => setOrderNumbers(prev => ({ ...prev, [oid]: o.orderNumber })))
                .catch(() => undefined);
        });
    }, [entry]);

    // Segment names for the line column, loaded only when some line carries a segment (best effort; falls back to the id).
    const lineHasSegment = !!entry?.lines?.some(l => !!l.segmentId);
    useEffect(() => {
        if (!lineHasSegment) return;
        SegmentService.getByInstitution(0, 200)
            .then(page => setSegmentNames(Object.fromEntries(((page?.content ?? []) as Segment[])
                .filter(s => !!s.id).map(s => [s.id!, `${s.code} - ${s.name}`]))))
            .catch(() => undefined);
    }, [lineHasSegment]);

    useEffect(() => {
        fetchEntry();
    }, [id]);

    // Maker-checker, mirrors JournalEntryService.assertPosterIsNotMaker: only the creator of a draft can be refused.
    const [makerBlock, setMakerBlock] = useState<string | null>(null);
    useEffect(() => {
        setMakerBlock(null);
        if (!canPost || entry?.status !== 'DRAFT' || !isCurrentUser(entry.createdBy)) return;
        let live = true;
        LedgerCloseSettingsService.get()
            .then(s => {
                if (!live) return;
                const total = (entry.lines ?? []).filter(l => l.entryType === 'DEBIT').reduce((sum, l) => sum + (l.amount ?? 0), 0);
                if (s.requireSeparatePoster) setMakerBlock('Your company requires a different person to post a journal than the one who created it. Ask another accountant to post this draft.');
                else if (s.separatePosterAbove != null && total > s.separatePosterAbove) setMakerBlock('Journals above the set amount must be posted by a different person than the one who created them. Ask another accountant to post this draft.');
            })
            .catch(() => undefined);
        return () => { live = false; };
    }, [entry, canPost]);

    const handlePost = async () => {
        if (!id) return;
        try {
            await JournalEntryService.postEntry(id);
            toast.showSuccess('Journal entry posted');
            await fetchEntry();
        } catch (err) {
            toast.showApiError(err, 'Failed to post journal entry');
            console.error('Error posting entry:', err);
        }
    };

    const handlePrint = async () => {
        if (!id) return;
        try {
            const blob = await JournalEntryService.downloadVoucherPdf(id);
            saveBlob(blob, `voucher-${entry?.entryNumber ?? id}.pdf`);
        } catch (err) {
            toast.showApiError(err, 'Failed to download voucher');
        }
    };

    const handleDuplicate = () => {
        if (!entry) return;
        navigate('/accounting/journal-entries/create', {
            state: {
                duplicateFrom: {
                    description: entry.description,
                    lines: (entry.lines ?? []).map(l => ({
                        accountId: l.accountId,
                        entryType: l.entryType,
                        amount: l.amount,
                        description: l.description,
                        segmentId: l.segmentId,
                        buyerOrderId: l.buyerOrderId, // B4-0 (M2)
                        departmentId: l.departmentId, // B4-H
                        projectId: l.projectId, // P1-A project
                        projectCode: l.projectCode, projectName: l.projectName, // label for a closed project
                    })),
                },
            },
        });
    };

    const handleMakeRecurring = () => {
        if (!entry) return;
        navigate('/accounting/recurring-journals/new', {
            state: {
                makeRecurringFrom: {
                    sourceEntryId: entry.id,
                    description: readableDescription(entry.description),
                    lines: (entry.lines ?? []).map(l => ({
                        accountId: l.accountId,
                        entryType: l.entryType,
                        amount: l.amount,
                        description: l.description,
                        segmentId: l.segmentId,
                        buyerOrderId: l.buyerOrderId, // B4-0 (M2)
                        departmentId: l.departmentId, // B4-H
                        projectId: l.projectId, // P1-A project
                        projectCode: l.projectCode, projectName: l.projectName, // label for a closed project
                    })),
                },
            },
        });
    };

    const handleVoid = async () => {
        if (!id) return;
        try {
            setVoiding(true);
            await JournalEntryService.voidEntry(id, voidReason.trim());
            toast.showSuccess('Journal entry voided');
            setVoidOpen(false);
            setVoidReason('');
            await fetchEntry();
        } catch (err) {
            toast.showApiError(err, 'Failed to void journal entry');
            console.error('Error voiding entry:', err);
        } finally {
            setVoiding(false);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!entry) {
        return <div style={{ color: 'red', padding: '20px' }}>Journal entry not found</div>;
    }

    const hasForeign = !!entry.lines?.some(l => l.currencyCode && l.foreignAmount != null);
    const hasOrders = !!entry.lines?.some(l => !!l.buyerOrderId); // B4-0 (M2)
    const hasDepartments = !!entry.lines?.some(l => !!l.departmentId); // B4-H
    const hasProjects = !!entry.lines?.some(l => !!l.projectId); // P1-A project
    const hasSegments = !!entry.lines?.some(l => !!l.segmentId);
    const totalDebit = entry.lines?.filter(l => l.entryType === 'DEBIT').reduce((sum, l) => sum + l.amount, 0) || 0;
    const totalCredit = entry.lines?.filter(l => l.entryType === 'CREDIT').reduce((sum, l) => sum + l.amount, 0) || 0;

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.journalDetail.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Journal Entry Detail</h2>
                <Button data-help="accounting.journalDetail.back" variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                    Back to List
                </Button>
            </div>

            {/* Entry Header */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <strong>Entry Number:</strong> {entry.entryNumber || '-'}
                    </div>
                    <div>
                        <strong>Date:</strong> {entry.date ? formatDate(entry.date) : '-'}
                    </div>
                    <div>
                        <strong>Description:</strong> {readableDescription(entry.description) || '-'}
                    </div>
                    <div>
                        <strong>Status:</strong>{' '}
                        <Chip label={humanizeEnum(entry.status)} color={getStatusColor(entry.status)} size="small" />
                        {entry.referenceType === 'CONTRA' && (
                            <Chip label="Contra" size="small" variant="outlined" color="info" sx={{ ml: 1 }} />
                        )}
                    </div>
                    {entry.referenceType && (
                        <div>
                            <strong>Reference:</strong>{' '}
                            <span>{referenceLabel(entry.referenceType)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Audit trail: who created / posted / voided */}
            <div data-testid="je-audit" style={{ marginBottom: '16px', fontSize: '0.9rem', color: '#555' }}>
                {[
                    entry.createdByName ? `Created by ${entry.createdByName}${entry.createdAt ? ` on ${formatDate(entry.createdAt)}` : ''}` : null,
                    entry.postedByName ? `Posted by ${entry.postedByName}${entry.postedAt ? ` on ${formatDateTime(entry.postedAt)}` : ''}` : null,
                    entry.voidedByName ? `${entry.status === 'VOID' ? 'Voided' : 'Reversed'} by ${entry.voidedByName}${entry.voidedAt ? ` on ${formatDateTime(entry.voidedAt)}` : ''}` : null,
                ].filter(Boolean).join(' · ')}
            </div>

            {/* Action Buttons */}
            <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <Button data-help="accounting.journalDetail.print" variant="outlined" onClick={handlePrint}>
                    Print voucher
                </Button>
                {canManage && (!entry.referenceType || entry.referenceType === 'CONTRA' || entry.referenceType === 'RECURRING_JOURNAL') && (
                    <Button data-help="accounting.journalDetail.duplicate" variant="outlined" onClick={handleDuplicate}>
                        Duplicate
                    </Button>
                )}
                {canManage && (!entry.referenceType || entry.referenceType === 'CONTRA' || entry.referenceType === 'RECURRING_JOURNAL') && (
                    <Button data-help="accounting.journalDetail.makeRecurring" variant="outlined" onClick={handleMakeRecurring}>
                        Make recurring
                    </Button>
                )}
                {canPost && entry.status === 'DRAFT' && (
                    <Button data-help="accounting.journalDetail.post" {...helpWhy(makerBlock ? { kind: 'custom', message: makerBlock } : null)} disabled={!!makerBlock} variant="contained" color="success" onClick={handlePost}>
                        Post Entry
                    </Button>
                )}
                {canPost && entry.status !== 'VOID' && entry.referenceType !== 'JOURNAL_REVERSAL' && !mustReverseFromSource(entry.referenceType) && (
                    <Button data-help="accounting.journalDetail.void" variant="contained" color="error" onClick={() => setVoidOpen(true)}>
                        Void Entry
                    </Button>
                )}
            </div>
            {entry.status !== 'VOID' && mustReverseFromSource(entry.referenceType) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This entry was created by another document ({referenceLabelInline(entry.referenceType)}).
                    To reverse it, reverse or cancel it from that document so both stay in step.
                </Alert>
            )}

            <Dialog data-help-page="accounting.journalDetail.voidForm.page" open={voidOpen} onClose={() => !voiding && setVoidOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Void this journal entry?</DialogTitle>
                <DialogContent>
                    {hasSourceDocument(entry.referenceType) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This entry came from another document ({referenceLabelInline(entry.referenceType)}).
                            Voiding it does not change that document, which will still show as done. Where the
                            document has its own reverse or cancel action, use that instead.
                        </Alert>
                    )}
                    {entry.status === 'POSTED' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A posted entry stays in the books. Voiding adds an opposite entry that cancels it out.
                        </Alert>
                    )}
                    <TextField data-help="accounting.journalDetail.voidForm.reason"
                        autoFocus
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label="Reason for voiding"
                        value={voidReason}
                        onChange={e => setVoidReason(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.journalDetail.voidForm.keep" onClick={() => setVoidOpen(false)} disabled={voiding}>Keep entry</Button>
                    <Button data-help="accounting.journalDetail.voidForm.confirm" color="error" variant="contained" onClick={handleVoid}
                        disabled={voiding || !voidReason.trim()}>
                        Void entry
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lines Table */}
            <h3 style={{ marginBottom: '12px' }}>Entry Lines</h3>
            <ScrollableTable>
            <table data-help-section="accounting.journalDetail.lines" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.journalDetail.col.code" style={tableHeaderStyle}>Account Code</th>
                        <th data-help="accounting.journalDetail.col.account" style={tableHeaderStyle}>Account Name</th>
                        <th data-help="accounting.journalDetail.col.debit" style={tableHeaderStyle}>Debit</th>
                        <th data-help="accounting.journalDetail.col.credit" style={tableHeaderStyle}>Credit</th>
                        {hasForeign && <th data-help="accounting.journalDetail.col.currency" style={tableHeaderStyle}>Original currency</th>}
                        {hasOrders && <th data-help="accounting.journalDetail.col.order" style={tableHeaderStyle}>Order</th>}
                        {hasDepartments && <th data-help="accounting.journalDetail.col.department" style={tableHeaderStyle}>Department</th>}
                        {hasProjects && <th data-help="accounting.journalDetail.col.project" style={tableHeaderStyle}>Project</th>}
                        {hasSegments && <th data-help="accounting.journalDetail.col.segment" style={tableHeaderStyle}>Segment</th>}
                        <th data-help="accounting.journalDetail.col.description" style={tableHeaderStyle}>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {entry.lines?.map((line, index) => (
                        <tr key={line.id || index}>
                            <td style={tableCellStyle}>{line.accountCode || '-'}</td>
                            <td style={tableCellStyle}>{line.accountName || '-'}</td>
                            <td style={tableCellStyle}>
                                {line.entryType === 'DEBIT' ? formatMoney(line.amount) : '-'}
                            </td>
                            <td style={tableCellStyle}>
                                {line.entryType === 'CREDIT' ? formatMoney(line.amount) : '-'}
                            </td>
                            {hasForeign && (
                                <td style={tableCellStyle} data-testid="line-foreign-amount">
                                    {line.currencyCode && line.foreignAmount != null
                                        ? `${line.currencyCode} ${formatMoney(line.foreignAmount)}${line.exchangeRate != null ? ` @ ${line.exchangeRate}` : ''}`
                                        : '-'}
                                </td>
                            )}
                            {hasOrders && (
                                <td style={tableCellStyle} data-testid="line-order">
                                    {line.buyerOrderId
                                        ? <RouterLink to={`/costing/orders/${line.buyerOrderId}`}>{orderNumbers[line.buyerOrderId] ?? line.buyerOrderId.slice(0, 8)}</RouterLink>
                                        : '-'}
                                </td>
                            )}
                            {hasDepartments && (
                                <td style={tableCellStyle} data-testid="line-department">
                                    {line.departmentId ? (line.departmentName ?? departmentNames[line.departmentId] ?? line.departmentId.slice(0, 8)) : '-'}
                                </td>
                            )}
                            {hasProjects && (
                                <td style={tableCellStyle} data-testid="line-project">
                                    {line.projectId ? (line.projectCode ? `${line.projectCode} ${line.projectName ?? ''}`.trim() : line.projectId.slice(0, 8)) : '-'}
                                </td>
                            )}
                            {hasSegments && (
                                <td style={tableCellStyle} data-testid="line-segment">
                                    {line.segmentId ? (segmentNames[line.segmentId] ?? line.segmentId.slice(0, 8)) : '-'}
                                </td>
                            )}
                            <td style={tableCellStyle}>{readableDescription(line.description) || '-'}</td>
                        </tr>
                    ))}
                    {/* Total Row */}
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        <td style={tableCellStyle} colSpan={2}>Total</td>
                        <td style={tableCellStyle}>{formatMoney(totalDebit)}</td>
                        <td style={tableCellStyle}>{formatMoney(totalCredit)}</td>
                        {hasForeign && <td style={tableCellStyle}></td>}
                        {hasOrders && <td style={tableCellStyle}></td>}
                        {hasDepartments && <td style={tableCellStyle}></td>}
                        {hasProjects && <td style={tableCellStyle}></td>}
                        {hasSegments && <td style={tableCellStyle}></td>}
                        <td style={tableCellStyle}></td>
                    </tr>
                </tbody>
            </table>
            </ScrollableTable>

            {id && <AttachmentsPanel entityType="JOURNAL_ENTRY" entityId={id} canManage={canManage} />}
        </div>
    );
};

export default JournalEntryDetail;
